import { useState } from 'react';
import { SearchIcon, CartIcon, HeartIcon, AccountIcon } from './Icons';

export default function TopBar({ onNavigate, activePage, categories = [], cartCount = 0, wishlistCount = 0, searchQuery = '', onSearchChange, products = [], unreadOrderCount = 0 }) {
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearchChange) onSearchChange(localSearch);
    setShowSuggestions(false);
    onNavigate('search', { query: localSearch });
  };

  const suggestions = localSearch.trim()
    ? products.filter(p => p.name.toLowerCase().includes(localSearch.toLowerCase())).slice(0, 5)
    : [];

  return (
    <>
      {/* ── Mobile/Tablet TopBar (md only, hidden on lg+) ── */}
      <header className="hidden md:flex lg:hidden items-center gap-3 px-4 py-3 bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="flex items-center flex-shrink-0 cursor-pointer" onClick={() => onNavigate('home')}>
          <img src="/logo.png" alt="Manna Bridal Logo" className="h-10 object-contain" />
        </div>
        <form onSubmit={handleSearch} className="flex-1 flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200 relative">
          <SearchIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={localSearch}
            onChange={e => { setLocalSearch(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Search for anything..."
            className="flex-1 bg-transparent text-sm outline-none text-gray-600 placeholder-gray-400"
          />
          {showSuggestions && localSearch.trim() && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
              {suggestions.length > 0 ? suggestions.map(s => (
                <div key={s.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowSuggestions(false); onNavigate('productdetail', { product: s }); }} className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 flex items-center gap-3">
                  <img src={s.image || (s.images && s.images[0])} alt={s.name} className="w-10 h-10 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{s.name}</p>
                    <p className="text-xs text-gray-500">{s.priceStr || `₦${Number(s.price).toLocaleString()}`}</p>
                  </div>
                </div>
              )) : (
                <div className="p-4 text-center text-sm text-gray-500">No products found</div>
              )}
            </div>
          )}
        </form>
        <div className="flex items-center gap-1">
          <button onClick={() => onNavigate('wishlist')} className={`p-2 rounded-xl hover:bg-gray-50 ${activePage === 'wishlist' ? 'text-[#F47B20]' : 'text-gray-500'}`}>
            <HeartIcon className="w-5 h-5" />
          </button>
          <button onClick={() => onNavigate('cart')} className={`p-2 rounded-xl hover:bg-gray-50 ${activePage === 'cart' ? 'text-[#F47B20]' : 'text-gray-500'}`}>
            <CartIcon className="w-5 h-5" />
          </button>
          <button onClick={() => onNavigate('account')} className={`p-2 rounded-xl hover:bg-gray-50 ${activePage === 'account' ? 'text-[#F47B20]' : 'text-gray-500'}`}>
            <AccountIcon className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* ── Desktop TopBar (lg+) — AliExpress style ── */}
      <div className="hidden lg:block sticky top-0 z-50">
        {/* Top strip */}
        <div className="bg-[#F47B20] text-white text-[11px] py-1 px-6 flex justify-between items-center">
          <span className="font-medium tracking-wide">✨ Premium Bridal Collections — Delivered Nationwide</span>
          <div className="flex items-center gap-4">
            <button onClick={() => onNavigate('orders')} className="hover:underline flex items-center gap-1 relative">
              My Orders
              {unreadOrderCount > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 rounded-full">{unreadOrderCount}</span>
              )}
            </button>
            <button onClick={() => onNavigate('account')} className="hover:underline">My Account</button>
          </div>
        </div>

        {/* Main header */}
        <header className="bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-[1600px] mx-auto px-8 py-3 flex items-center gap-6">
            {/* Logo */}
            <div className="flex-shrink-0 cursor-pointer" onClick={() => onNavigate('home')}>
              <img src="/logo.png" alt="Manna Bridal" className="h-12 object-contain" />
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex-1 flex max-w-3xl relative">
              <input
                type="text"
                value={localSearch}
                onChange={e => { setLocalSearch(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Search bridal gowns, veils, accessories..."
                className="flex-1 px-4 py-2.5 text-sm border-2 border-[#F47B20] border-r-0 rounded-l-full outline-none bg-white text-gray-800 placeholder-gray-400"
              />
              <button type="submit" className="px-6 bg-[#F47B20] hover:bg-[#D96A10] text-white rounded-r-full text-sm font-bold flex items-center gap-2 transition-colors">
                <SearchIcon className="w-4 h-4" />
                Search
              </button>
              {showSuggestions && localSearch.trim() && (
                <div className="absolute top-full left-0 right-12 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                  {suggestions.length > 0 ? suggestions.map(s => (
                    <div key={s.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowSuggestions(false); onNavigate('productdetail', { product: s }); }} className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 flex items-center gap-3">
                      <img src={s.image || (s.images && s.images[0])} alt={s.name} className="w-10 h-10 object-cover rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">{s.name}</p>
                        <p className="text-xs text-gray-500">{s.priceStr || `₦${Number(s.price).toLocaleString()}`}</p>
                      </div>
                    </div>
                  )) : (
                    <div className="p-4 text-center text-sm text-gray-500">No products found</div>
                  )}
                </div>
              )}
            </form>

            {/* Icons */}
            <div className="flex items-center gap-5 flex-shrink-0">
              <button onClick={() => onNavigate('account')} className="flex flex-col items-center gap-0.5 text-gray-600 hover:text-[#F47B20] transition-colors group">
                <AccountIcon className="w-6 h-6" />
                <span className="text-[10px] font-medium">Account</span>
              </button>
              <button onClick={() => onNavigate('wishlist')} className="flex flex-col items-center gap-0.5 relative text-gray-600 hover:text-[#F47B20] transition-colors">
                <HeartIcon className="w-6 h-6" />
                <span className="text-[10px] font-medium">Wishlist</span>
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center">{wishlistCount}</span>
                )}
              </button>
              <button onClick={() => onNavigate('cart')} className="flex flex-col items-center gap-0.5 relative text-gray-600 hover:text-[#F47B20] transition-colors">
                <CartIcon className="w-6 h-6" />
                <span className="text-[10px] font-medium">Cart</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#F47B20] text-white rounded-full text-[9px] font-bold flex items-center justify-center">{cartCount}</span>
                )}
              </button>
            </div>
          </div>

          {/* Category Nav ribbon */}
          <div className="border-t border-gray-100 bg-white">
            <div className="max-w-[1600px] mx-auto px-8 flex items-center gap-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => onNavigate('shop')}
                className={`flex-shrink-0 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activePage === 'shop' ? 'border-[#F47B20] text-[#F47B20]' : 'border-transparent text-gray-600 hover:text-[#F47B20] hover:border-[#F47B20]/40'}`}
              >
                All Products
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => onNavigate('categorydetail', { category: cat })}
                  className="flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-600 hover:text-[#F47B20] hover:border-[#F47B20]/40 transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </button>
              ))}
              <button
                onClick={() => onNavigate('orders')}
                className="flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-[#F47B20] hover:border-[#F47B20]/40 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                My Orders
                {unreadOrderCount > 0 && (
                  <span className="bg-[#F47B20] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadOrderCount}</span>
                )}
              </button>
            </div>
          </div>
        </header>
      </div>
    </>
  );
}
